import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Spinner,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@wc/domain/authn";
import { listUserOrganizations } from "@wc/domain/organizations";
import { ReactComponent as PeopleIcon } from "@wc/ui/assets/icons/people.svg";

/**
 * A dropdown component for switching between organizations
 * @param {object} props Component props
 * @param {React.ReactNode} props.children The trigger element
 * @returns {React.ReactElement} The org switcher dropdown
 */
export default function OrgSwitcher({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authContext = useAuth();
  const [orgs, setOrgs] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);

  const currentOrg = authContext.getCurrentUser().activeOrganization;

  const fetchOrgs = React.useCallback(async () => {
    if (hasLoaded || isLoading) return;

    setIsLoading(true);
    try {
      const userOrgs = await listUserOrganizations(
        authContext.getCurrentUser().id,
      );
      setOrgs(userOrgs);
      setHasLoaded(true);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [authContext, hasLoaded, isLoading]);

  const handleSwitch = async (orgId) => {
    if (orgId === currentOrg?.id) return;

    try {
      await authContext.switchOrganization(orgId);
      navigate("/");
    } catch (error) {
      console.error("Failed to switch organization:", error);
    }
  };

  return (
    <Dropdown onOpenChange={(isOpen) => isOpen && fetchOrgs()}>
      <DropdownTrigger>{children}</DropdownTrigger>
      <DropdownMenu
        aria-label="Organization switcher"
        className="w-[280px]"
        selectionMode="single"
        selectedKeys={currentOrg ? new Set([currentOrg.id]) : new Set()}
        onSelectionChange={(keys) => {
          const selectedId = Array.from(keys)[0];
          if (
            selectedId &&
            selectedId !== "loading" &&
            selectedId !== "all-orgs"
          ) {
            handleSwitch(selectedId);
          }
        }}
        disabledKeys={isLoading ? ["loading"] : []}
      >
        <DropdownSection title={t("nav.switchOrg")} showDivider>
          {isLoading ? (
            <DropdownItem key="loading" className="flex justify-center">
              <Spinner size="sm" />
            </DropdownItem>
          ) : (
            orgs.map((item) => (
              <DropdownItem
                key={item.org.id}
                description={item.role}
                startContent={
                  <PeopleIcon
                    className="size-5"
                    role="img"
                    aria-label="organization"
                  />
                }
                className={
                  item.org.id === currentOrg?.id ? "bg-primary/10" : ""
                }
              >
                {item.org.displayName}
              </DropdownItem>
            ))
          )}
        </DropdownSection>
        <DropdownSection>
          <DropdownItem
            key="all-orgs"
            href="/orgs"
            description={t("nav.viewAllOrgsDesc")}
          >
            {t("nav.viewAllOrgs")}
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
}
